import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import OrderFulfillmentsManager from '../../../../services/api/OrderFulfillmentsManager';
import { DeliveryProvider, OrderMethod } from '../../../../services/exports/Constants';
import { useOngoingOrders } from '../../../../providers/OngoingOrdersProvider';
import { useHelpers } from '../../../../providers/HelpersProvider';
import ConfirmationModal from '../../../ConfirmationModal';
import { Button, Collapse } from 'antd';
import FooterWrapper from './FooterWrapper';
import { DateTime } from 'luxon';
import OrderTimeChangeInput from '../../../../components/order/OrderTimeChangeInput';

export default function OrderTimeChange({ order, onError }) {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:Order:OrderDetails:Footers:OrderTimeChange' });

  const { formatTime } = useHelpers();
  const { replaceOrderData, toggleOrderDetailsModal } = useOngoingOrders();

  const [preparationTime, setPreparationTime] = useState(order.estimated_preparation_time);
  const [loading, setLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const currentPickupTime = DateTime.fromISO(order.estimated_pickup_time);
  const newPickupTime = DateTime.fromISO(order.paid_at)
    .plus({ minutes: preparationTime + 1 })
    .startOf('minute');

  async function onConfirm(requireConfirmation = true) {
    if (requireConfirmation) {
      return setShowConfirmationModal(true);
    }

    setLoading(true);
    const { success, data } = await OrderFulfillmentsManager.update(order.fulfillment_id, {
      estimated_preparation_time: preparationTime,
    });
    setLoading(false);

    if (!success) {
      return onError(data?.message);
    }

    setShowConfirmationModal(false);
    replaceOrderData(data.data);
    toggleOrderDetailsModal();
  }

  return (
    <FooterWrapper className="px-sm">
      {order?.method === OrderMethod.Delivery && order?.delivery_provider !== DeliveryProvider.Standalone ? (
        <span className="block w-full text-center">
          <Trans
            t={t}
            values={{
              time: formatTime(order?.delivery?.pickup_estimate),
              confidenceLabel: t(`confidence_labels.${order.is_driver_assigned ? 'confirmed' : 'estimated'}`),
            }}
          >
            text.delivery.external
          </Trans>
        </span>
      ) : (
        <Collapse
          ghost
          expandIconPlacement="end"
          items={[
            {
              key: '1',
              classNames: {
                header: '!py-none text-base font-medium',
                body: '!py-sm',
              },
              label: t('title'),
              children: (
                <div className="space-y-sm">
                  <p className="text-center">
                    <Trans
                      t={t}
                      values={{
                        date: currentPickupTime.hasSame(DateTime.local(), 'day')
                          ? t('labels.today')
                          : currentPickupTime.toFormat('cccc, dd LLL'),
                        time: formatTime(currentPickupTime),
                        confidence: t(`confidence_labels.confirmed`),
                      }}
                    >
                      {order.method === OrderMethod.Delivery ? `text.${order.method}.internal` : `text.${order.method}`}
                    </Trans>
                  </p>
                  <OrderTimeChangeInput
                    order={order}
                    preparationTime={preparationTime}
                    setPreparationTime={setPreparationTime}
                  />
                  <Button
                    type="primary"
                    onClick={() => onConfirm()}
                    className="w-full"
                    disabled={newPickupTime.valueOf() === currentPickupTime.valueOf()}
                    loading={loading}
                  >
                    {order.method === OrderMethod.Delivery
                      ? t('buttons.confirm_delivery')
                      : t('buttons.confirm_pickup')}
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}
      <ConfirmationModal
        show={showConfirmationModal}
        description={t('confirmation_modal.description', {
          currentPickupTime: formatTime(currentPickupTime),
          newPickupTime: formatTime(newPickupTime),
        })}
        cancelButtonText={t('confirmation_modal.cancel_button_text')}
        confirmationButtonText={t('confirmation_modal.ok_button_text')}
        onConfirm={() => onConfirm(false)}
        onCancel={() => setShowConfirmationModal(false)}
        confirming={loading}
      />
    </FooterWrapper>
  );
}
